import React, { useState, useEffect } from "react";
import Banner from "./homePageComponents/Banner";
import CategoryFeatures from "./homePageComponents/CategoryFeatures";
import WelcomeSection from "./homePageComponents/WelcomeSection";
import HomeBookSection from "./homePageComponents/HomeBookSection";
import FeaturedAuthors from "./homePageComponents/FeaturedAuthors";
import BookManager from "../../services/home/BookManager";
import AuthorManager from "../../services/home/AuthorManager";
import BookHighlightManager from "../../services/home/BookHighlightManager";
import BookOfTheMonth from "./homePageComponents/BookOfTheMonth";
import HowItWorks from "./homePageComponents/HowItWorks";
import TestimonialsSection from "./homePageComponents/TestimonialsSection";
import CategoriesSection from "./homePageComponents/CategoriesSection";
import { useNavigate } from "react-router-dom";
import StaggeredBookBanner from "./homePageComponents/StaggeredBookBanner";
import { Link } from "react-router-dom";

export default function HomePage() {
  const [trendingBooks, setTrendingBooks] = useState([]);
  const [justLaunchedBooks, setJustLaunchedBooks] = useState([]);
  const [bestReadsBooks, setbestReadsBooks] = useState([]);
  const [topReadsBooks, setTopReadsBooks] = useState([]);
  const [bookOfTheMonth, setBookOfTheMonth] = useState(null);
  const [bookOfMonthDetails, setBookOfMonthDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchHighlightedBooks = async () => {
    try {
      // 1️⃣ Fetch highlights and authors in parallel
      const [
        trendingHighlights,
        justLaunchedHighlights,
        bestReadsHighlights,
        authorsRes,
      ] = await Promise.all([
        BookHighlightManager.getTrendingBooks(),
        BookHighlightManager.getJustLaunchedBooks(),
        BookHighlightManager.getBestReadsBooks(),
        AuthorManager.getAllAuthors(),
      ]);
  
      // 2️⃣ Build authorMap before using it anywhere
      const authors =
        authorsRes.responseCode === 200 && authorsRes.errorCode === null
          ? authorsRes.responseData || []
          : [];
  
      const authorMap = {};
      authors.forEach((author) => {
        authorMap[author.authorId] = author.authorName;
      });
  
      // 3️⃣ Map “Top Rated” using authorMap
      const topRatedList = await BookManager.getTopRatedBooks(10, null);
      const topReadsMapped = topRatedList.map((b) => ({
        id: b.bookId,
        image: b.bookImages?.[0] || "defaultImage.jpg",
        title: b.bookName,
        author: authorMap[b.authorId] || "Unknown",
        isbn: b.bookIsbn10,
        rating: b.averageRating,
        inStock: b.bookStockStatus,
      }));
  
      // 4️⃣ Helper to map highlight directly (no extra fetch)
      const mapToBookCard = (book) => ({
        id: book.bookId,
        image: book.bookImages?.[0] || "defaultImage.jpg",
        title: book.bookName,
        author: authorMap[book.authorId] || "Unknown",
        isbn: book.bookIsbn10,
        rating: book.averageRating,
        inStock: book.bookStockStatus,
      });

      // 5️⃣ Map highlights (no need for Promise.all)
      const trendingBooksMapped = trendingHighlights.map(mapToBookCard);
      const justLaunchedBooksMapped = justLaunchedHighlights.map(mapToBookCard);
      const bestReadsMapped = bestReadsHighlights.map(mapToBookCard);

      // 6️⃣ Push into state
      setTrendingBooks(trendingBooksMapped);
      setJustLaunchedBooks(justLaunchedBooksMapped);
      setbestReadsBooks(bestReadsMapped);
      setTopReadsBooks(topReadsMapped);
    } catch (err) {
      console.error("Error fetching highlighted books:", err);
      setError("Failed to load highlighted books.");
    }
  };


  const fetchBookOfTheMonth = async () => {
    try {
      const botm = await BookHighlightManager.getBookOfTheMonth();
      setBookOfTheMonth(botm);
      if (botm?.bookId) {
        const fullBook = await BookManager.getBookById(botm.bookId);
        setBookOfMonthDetails(fullBook);
      }
    } catch (err) {
      console.error("Error fetching book of the month:", err);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      await Promise.all([fetchHighlightedBooks(), fetchBookOfTheMonth()]);
      setLoading(false);
    };

    fetchAll();
  }, []);

  return (
    <div>
      <Banner />

      <CategoryFeatures className="sm:-mt-6 mt-4" />

      {/* <WelcomeSection />
      <div className="mb-16" /> */}

      <section className="w-full bg-white py-6 overflow-x-hidden">
        <div className="max-w-screen-xl mx-auto px-4 md:px-10 space-y-2">
          <HomeBookSection
            title="Trending Books"
            books={trendingBooks}
            onSeeAll={() => navigate("/trending-books")}
            loading={loading}
          />

          <HomeBookSection
            title="Just Launched"
            books={justLaunchedBooks}
            onSeeAll={() => navigate("/justlaunched-books")}
            loading={loading}
          />

          {/* Hero Intro Section */}
          <section className="max-w-5xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2 space-y-6">
              <h1 className="text-4xl lg:text-5xl font-extrabold text-[#EB5E22]">
                Find the Perfect Plan for Your Reading Style
              </h1>
              <p className="text-gray-700 text-lg leading-relaxed">
                Whether you’re a casual weekend reader or a bookworm, our
                membership plans are designed to fit your reading pace and
                preferences. Enjoy unlimited book rentals, free delivery, and
                pickup—tailored just for you.
              </p>
              <button
                onClick={() => navigate("/membershipPlans")}
                className="px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 bg-primary text-white rounded-lg text-xs sm:text-sm md:text-base font-semibold shadow-md hover:bg-primary-dark transition flex items-center justify-center gap-1"
              >
                <span>Compare All Plans Now</span>
                <span className="text-sm sm:text-base">→</span>
              </button>
            </div>
            <div className="lg:w-2/3">
              <img
                src="/images/mem.avif"
                alt="Membership plans illustration"
                className="w-full h-auto rounded-2xl lg:max-w-[90%] mx-auto"
                loading="lazy"
              />
            </div>
          </section>

          <HomeBookSection
            title="Best Reads"
            books={bestReadsBooks}
            onSeeAll={() => navigate("/bestreads-books")}
            loading={loading}
          />

          <HomeBookSection
            title="Top Rated"
            books={topReadsBooks}
            onSeeAll={() => navigate("/top-rated-books")}
            loading={loading}
          />

          <FeaturedAuthors />
        </div>
      </section>
      <StaggeredBookBanner />

      <div className="mt-16">
        <BookOfTheMonth book={bookOfMonthDetails} />
      </div>

      <HowItWorks />

      <TestimonialsSection />
    </div>
  );
}
